import * as React from 'react';

import { Dispatch } from 'redux';
import { connect } from 'react-redux';
import { push, CallHistoryMethodAction } from 'connected-react-router';

import AppState from '@webapp/store/state';

import { Translate } from 'react-localize-redux';

import styled from 'styled-components';

import NewMenu from '@components/new-menu';
import Switch from '@components/basics/switch';
import AddIcon from '@components/basics/icons/add';
import Typography from '@components/basics/typography';
import UserInfoItem from '@components/new-menu/new-menu-item/user-info-item';

import LabelMenuSelectorCtrl from '@webapp/controllers/label/label-menu-selector';
import NavbarUserAvatarCtrl from '@webapp/controllers/navbar/navbar-actions/navbar-items/navbar-user-avatar';

import SimpleArtist from '@common/types/artist/simple-artist';

import { Theme } from '@webapp/store/ui/types';
import { ChangeTheme, CloseMenu } from '@webapp/store/ui/action-creators';
import CirclePlaceholder from '@components/basics/circle-placeholder';
import User from '@common/types/user/user';

interface ArtistMenuProps {
  className?: string;
  artist?: SimpleArtist;
  user?: Partial<User>;
  currentTheme: Theme;
  isMobile?: boolean;
  changeTheme: (theme: Theme) => ChangeTheme;
  createNewArtist: () => CallHistoryMethodAction;
  closeMenu: () => CloseMenu;
}

const mapStateToProps = (state: AppState) => ({
  artist: state.artist.active,
  user: state.user.profile,
  currentTheme: state.ui.theme,
  isMobile: state.ui.mobile,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  createNewArtist: () => dispatch(push('/artist/new')),
  changeTheme: (theme: Theme) =>
    dispatch(new ChangeTheme(theme === 'light' ? 'dark' : 'light')),
  closeMenu: () => dispatch(new CloseMenu()),
});

export const ArtistMenu: React.FC<ArtistMenuProps> = ({
  className,
  currentTheme,
  artist,
  user,
  isMobile,
  closeMenu,
  changeTheme,
  createNewArtist,
}) => {
  return (
    <NewMenu
      className={className}
      button={
        artist ? (
          <NavbarUserAvatarCtrl isMobile artist={artist} onHeader />
        ) : user ? (
          <NavbarUserAvatarCtrl isMobile user={user} onHeader />
        ) : isMobile ? (
          <CirclePlaceholder size={40} />
        ) : (
          <ButtonPlaceholder />
        )
      }
      title="DASHBOARD.NEW_DASHBOARD.USER.MENU.TITLE"
      menuId="ARTIST_MENU"
      normalPadding
      topHeader={
        isMobile ? (
          <Typography type="subheader">
            <Translate id="DASHBOARD.ARTIST_MENU.TITLE" />
          </Typography>
        ) : null
      }
    >
      <UserInfoItem />
      <Divider />
      <ThemeToggler>
        <Switch
          label={<Translate id="DASHBOARD.ARTIST_MENU.THEME.MODE" />}
          active={currentTheme === 'dark'}
          onClick={() => changeTheme(currentTheme)}
          isBig
        />
      </ThemeToggler>
      <Divider />
      <ProfilesWrapper>
        <LabelMenuSelectorCtrl onChange={closeMenu} />
      </ProfilesWrapper>
      <AddArtistBtn onClick={createNewArtist}>
        <AddIcon className="add-icon" />
        <Typography weight="700" className="add-text">
          <Translate id="ARTIST_PROFILE.ADDARTIST.TITLE" />
        </Typography>
      </AddArtistBtn>
    </NewMenu>
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(styled(ArtistMenu)`
  padding: 0;

  .more-artist-needed {
    margin-bottom: 16px;
    ${({ theme }) => `@media screen and ${theme.device.tablet}`} {
      margin-right: 72px;
      margin-bottom: 48px;
    }
  }
`);

const AddArtistBtn = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  cursor: pointer;
  margin-block: 16px;

  ${({ theme }) => `@media screen and ${theme.device.tablet}`} {
    margin-right: 72px;
  }

  .add-icon {
    margin-right: 16px;
  }

  .add-text {
    color: ${({ theme }) => theme.colors.accent};
  }
`;

const ProfilesWrapper = styled.div`
  overflow-y: scroll;
  height: auto;
`;

const ThemeToggler = styled.div`
  margin: 16px 0;
  ${({ theme }) => `@media screen and ${theme.device.tablet}`} {
    padding-right: 72px;
  }
`;

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background-color: ${({ theme }) => theme.colors.primary};
  opacity: 0.1;
`;

const ButtonPlaceholder = styled.div`
  width: 136px;
  height: 40px;
  padding: 5px 10px 5px 5px;
  border-radius: 90px;
  background-color: ${({ theme }) => theme.colors.midGray};
`;
